import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

// Reusable fuzzy "did you mean an existing org?" nudge for an IMO/Agency text
// input (Phase 2b). Debounce-queries /organizations/suggestion and renders a
// "Did you mean X? [Use it]" chip; "Use it" fills the input. Free-text is always
// preserved — this only nudges toward an existing org to curb name drift.
//
// Wire on a wrapper element:
//   data-controller="org-suggestion"
//   data-org-suggestion-url-value / -organization-type-value ("imo"|"agency")
//   data-org-suggestion-parent-id-value (optional, agency scoping)
// with an input target (action "input->org-suggestion#query") and a
// suggestion target (empty container for the nudge).
// Both inline choices ("Use it", and one per candidate parent IMO) are actions
// the agent takes, so they wear the Secondary tier — the outlined white
// surface — never the transparent Utility tier ("we do not have any flat
// buttons", owner rule 2026-09-10; PE-032 / R-15, campaign #12564). Compact
// (xs) because they sit inline in a `small` helper line beside the prose.
const CHOICE_BUTTON_CLASS = "gl-btn gl-btn-outline gl-btn-xs ms-1"

export default class extends Controller {
  static targets = ["input", "suggestion"]
  static values = {
    url: String,
    organizationType: String,
    parentId: String,
    minChars: { type: Number, default: 3 },
    debounce: { type: Number, default: 300 },
  }

  disconnect() {
    clearTimeout(this._timer)
  }

  query() {
    clearTimeout(this._timer)
    this._timer = setTimeout(() => this.fetchSuggestion(), this.debounceValue)
  }

  async fetchSuggestion() {
    // Bump the sequence token FIRST — before the min-chars early return — so
    // every call (including a shrink below the floor) invalidates any earlier
    // in-flight request. Otherwise a slow response from a longer query could
    // repaint a stale suggestion over now-shortened/empty text. Mirrors
    // agent_search_controller.js, which bumps its token per keystroke.
    const seq = (this.seq = (this.seq || 0) + 1)

    const name = this.inputTarget.value.trim()
    if (name.length < this.minCharsValue) return this.clear()

    const query = { name, organization_type: this.organizationTypeValue }
    if (this.hasParentIdValue && this.parentIdValue) query.parent_id = this.parentIdValue

    try {
      const response = await get(this.urlValue, { query, responseKind: "json" })
      if (seq !== this.seq || !response.ok) return

      const data = await response.json
      if (seq !== this.seq) return

      // A misclassification (the typed IMO name is actually an agency) takes
      // priority over a fuzzy "did you mean?" — it's a stronger, structural signal.
      if (data && data.misclassification) {
        this.renderMisclassification(data.misclassification)
        return
      }

      const suggestion = data && data.suggestion
      // Don't nudge toward something they've already typed verbatim.
      if (suggestion && suggestion.name.toLowerCase() !== name.toLowerCase()) {
        this.render(suggestion.name)
      } else {
        this.clear()
      }
    } catch {
      // Network/parse failure — leave the field as-is, no nudge.
    }
  }

  use(event) {
    event.preventDefault()
    this.inputTarget.value = event.currentTarget.dataset.orgName
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.clear()
  }

  render(name) {
    if (!this.hasSuggestionTarget) return
    this.clear()

    const wrap = document.createElement("div")
    wrap.className = "small text-muted mt-1"

    const strong = document.createElement("strong")
    strong.textContent = name

    const button = document.createElement("button")
    button.type = "button"
    button.className = CHOICE_BUTTON_CLASS
    button.textContent = "Use it"
    button.dataset.orgName = name
    button.dataset.action = "org-suggestion#use"

    wrap.append(document.createTextNode("Did you mean "), strong, document.createTextNode("? "), button)
    this.suggestionTarget.appendChild(wrap)
  }

  // The typed IMO name is actually a known agency (Phase 3c). Render a stronger,
  // structural nudge offering to set the IMO to the agency's real parent(s) — one
  // "Fix" button per candidate parent IMO. Choosing one dispatches a bubbling
  // "org-suggestion:fix-misclassification" event so each host surface (the
  // confirmation modal, onboarding, settings) can set both fields its own way.
  renderMisclassification(misclassification) {
    if (!this.hasSuggestionTarget) return
    this.clear()

    const agencyName = misclassification.agency_name
    const imos = misclassification.suggested_imos || []
    if (!agencyName || imos.length === 0) return

    const wrap = document.createElement("div")
    wrap.className = "small text-warning mt-1"

    const strong = document.createElement("strong")
    strong.textContent = agencyName
    wrap.append(strong, document.createTextNode(" is an agency, not an IMO. Set your IMO to: "))

    imos.forEach((imo) => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = CHOICE_BUTTON_CLASS
      button.textContent = imo.name
      button.dataset.imoName = imo.name
      button.dataset.agencyName = agencyName
      button.dataset.action = "org-suggestion#fixMisclassification"
      wrap.appendChild(button)
    })

    this.suggestionTarget.appendChild(wrap)
  }

  fixMisclassification(event) {
    event.preventDefault()
    const { imoName, agencyName } = event.currentTarget.dataset
    this.dispatch("fix-misclassification", { detail: { imoName, agencyName } })
    this.clear()
  }

  clear() {
    if (this.hasSuggestionTarget) this.suggestionTarget.innerHTML = ""
  }
}
